import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, map } from 'rxjs';
import { AbstractService } from '../../../core/AbstractService';
import { AppService } from '../../../core/app.service';
import { ApiResponse } from '../../../data/http/responses/apiresponse.response';
import { env } from '../../../../env/env';
import {
  AccesKeycloakResponse,
  CompteKeycloakPourPersonneExistanteRequest,
  GroupeKeycloakRequest,
  GroupRepresentation,
  MotDePasseRequest,
  RoleKeycloakRequest,
  RoleNoeudDto,
  RoleRepresentation,
  UserRepresentation,
  UserSessionRepresentation,
  UtilisateurCompletRequest,
  UtilisateurCompletUpdateRequest,
  UtilisateurEnrichiDto,
} from '../../../pages/app/model/keyclaok/adminservice/keycloak-admin.model';

/**
 * Service Angular — miroir de KeycloakAdminController
 * Base URL : /v0.1/api/simo/keycloak-admin
 *
 * Toutes les méthodes retournent Observable<T> directement.
 * L'extraction de response.data est gérée par les helpers privés get/post/put/patch/delete
 * définis plus bas (remplacent BaseApiService).
 */
@Injectable({ providedIn: 'root' })
export class KeycloakAdminService extends AbstractService<any, any> {

  private readonly BASE = 'keycloak-admin';

  constructor(http: HttpClient, appService: AppService) {
    super(`${env.apiUrl}`, http, null, appService);
  }

  // ==========================================================================
  // Helpers HTTP génériques — remplacent ceux hérités de BaseApiService.
  // Reconstituent le même contrat (Observable<T> déjà déballé de ApiResponse.data),
  // donc aucune des méthodes métier ci-dessous n'a besoin d'être modifiée.
  // ==========================================================================

  private httpGet<T>(path: string, options?: { params?: HttpParams }): Observable<T> {
    return this.http.get<ApiResponse<T>>(`${this.baseUrl}${path}`, { headers: this.getHeaders(), ...options })
      .pipe(map(res => res.data as T), catchError(this.handleError));
  }

  private httpPost<T>(path: string, body: any, options?: { params?: HttpParams }): Observable<T> {
    return this.http.post<ApiResponse<T>>(`${this.baseUrl}${path}`, body, { headers: this.getHeaders(), ...options })
      .pipe(map(res => res.data as T), catchError(this.handleError));
  }

  private httpPut<T>(path: string, body: any, options?: { params?: HttpParams }): Observable<T> {
    return this.http.put<ApiResponse<T>>(`${this.baseUrl}${path}`, body, { headers: this.getHeaders(), ...options })
      .pipe(map(res => res.data as T), catchError(this.handleError));
  }

  private httpPatch<T>(path: string, body: any, options?: { params?: HttpParams }): Observable<T> {
    return this.http.patch<ApiResponse<T>>(`${this.baseUrl}${path}`, body, { headers: this.getHeaders(), ...options })
      .pipe(map(res => res.data as T), catchError(this.handleError));
  }

  private httpDelete<T>(path: string, options?: { params?: HttpParams; body?: any }): Observable<T> {
    return this.http.delete<ApiResponse<T>>(`${this.baseUrl}${path}`, { headers: this.getHeaders(), ...options })
      .pipe(map(res => res.data as T), catchError(this.handleError));
  }

  // ==========================================================================
  // UTILISATEURS — CRUD
  // ==========================================================================

  /**
   * Crée atomiquement Personne + Relations + Utilisateur SIMO + compte KC.
   * POST /keycloak-admin/utilisateurs
   */
  creerUtilisateur(request: UtilisateurCompletRequest): Observable<Record<string, string>> {
    return this.httpPost<Record<string, string>>(`${this.BASE}/utilisateurs`, request);
  }

  /**
   * Crée un compte KC + Utilisateur SIMO pour une Personne déjà en BDD.
   * POST /keycloak-admin/utilisateurs/depuis-personne-existante
   */
  creerCompteDepuisPersonneExistante(
    request: CompteKeycloakPourPersonneExistanteRequest,
  ): Observable<Record<string, string>> {
    return this.httpPost<Record<string, string>>(
      `${this.BASE}/utilisateurs/depuis-personne-existante`,
      request,
    );
  }

  /**
   * Mise à jour partielle KC + BDD (seuls les champs non-null sont appliqués).
   * PUT /keycloak-admin/utilisateurs/{keycloakUserId}
   */
  mettreAJourUtilisateur(
    keycloakUserId: string,
    request: UtilisateurCompletUpdateRequest,
  ): Observable<Record<string, string>> {
    return this.httpPut<Record<string, string>>(
      `${this.BASE}/utilisateurs/${keycloakUserId}`,
      request,
    );
  }

  /**
   * Supprime KC + Utilisateur SIMO. Personne optionnellement supprimée.
   * DELETE /keycloak-admin/utilisateurs/{keycloakUserId}?supprimerPersonne=false
   */
  supprimerUtilisateur(
    keycloakUserId: string,
    supprimerPersonne = false,
  ): Observable<Record<string, string>> {
    const params = new HttpParams().set('supprimerPersonne', supprimerPersonne);
    return this.httpDelete<Record<string, string>>(
      `${this.BASE}/utilisateurs/${keycloakUserId}`,
      { params },
    );
  }

  // ==========================================================================
  // UTILISATEURS — Consultation
  // ==========================================================================

  /** GET /keycloak-admin/utilisateurs/{keycloakUserId} */
  getUtilisateur(keycloakUserId: string): Observable<UserRepresentation> {
    return this.httpGet<UserRepresentation>(`${this.BASE}/utilisateurs/${keycloakUserId}`);
  }

  /** GET /keycloak-admin/utilisateurs/lister (100 max) */
  listerUtilisateurs(): Observable<UserRepresentation[]> {
    return this.httpGet<UserRepresentation[]>(`${this.BASE}/utilisateurs/lister`);
  }

  /** GET /keycloak-admin/utilisateurs/lister/pagine?first=0&max=20 */
  listerUtilisateursPagine(first = 0, max = 20): Observable<UserRepresentation[]> {
    const params = new HttpParams().set('first', first).set('max', max);
    return this.httpGet<UserRepresentation[]>(
      `${this.BASE}/utilisateurs/lister/pagine`,
      { params },
    );
  }

  /** GET /keycloak-admin/utilisateurs/compter */
  compterUtilisateurs(): Observable<number> {
    return this.httpGet<number>(`${this.BASE}/utilisateurs/compter`);
  }

  /** GET /keycloak-admin/utilisateurs/rechercher?q= */
  rechercherUtilisateurs(q: string): Observable<UserRepresentation[]> {
    const params = new HttpParams().set('q', q);
    return this.httpGet<UserRepresentation[]>(
      `${this.BASE}/utilisateurs/rechercher`,
      { params },
    );
  }

  /** GET /keycloak-admin/utilisateurs/rechercher/avance */
  rechercherUtilisateursAvance(filters: {
    username?: string;
    email?: string;
    prenom?: string;
    nom?: string;
    exact?: boolean;
    first?: number;
    max?: number;
  }): Observable<UserRepresentation[]> {
    let params = new HttpParams();
    if (filters.username != null) params = params.set('username', filters.username);
    if (filters.email != null) params = params.set('email', filters.email);
    if (filters.prenom != null) params = params.set('prenom', filters.prenom);
    if (filters.nom != null) params = params.set('nom', filters.nom);
    if (filters.exact != null) params = params.set('exact', filters.exact);
    if (filters.first != null) params = params.set('first', filters.first);
    if (filters.max != null) params = params.set('max', filters.max);
    return this.httpGet<UserRepresentation[]>(
      `${this.BASE}/utilisateurs/rechercher/avance`,
      { params },
    );
  }

  // ==========================================================================
  // UTILISATEURS — Actions sur le compte
  // ==========================================================================

  /** PUT /keycloak-admin/utilisateurs/{id}/attributs */
  mettreAJourAttributs(
    keycloakUserId: string,
    attributs: Record<string, string[]>,
  ): Observable<void> {
    return this.httpPut<void>(
      `${this.BASE}/utilisateurs/${keycloakUserId}/attributs`,
      attributs,
    );
  }

  /** PATCH /keycloak-admin/utilisateurs/{id}/attributs/{cle} */
  ajouterOuRemplacerAttribut(
    keycloakUserId: string,
    cle: string,
    valeurs: string[],
  ): Observable<void> {
    return this.httpPatch<void>(
      `${this.BASE}/utilisateurs/${keycloakUserId}/attributs/${cle}`,
      valeurs,
    );
  }

  /** PUT /keycloak-admin/utilisateurs/{id}/mot-de-passe */
  changerMotDePasse(
    keycloakUserId: string,
    request: MotDePasseRequest,
  ): Observable<void> {
    return this.httpPut<void>(
      `${this.BASE}/utilisateurs/${keycloakUserId}/mot-de-passe`,
      request,
    );
  }

  /** PUT /keycloak-admin/utilisateurs/{id}/activer?activer=true|false */
  changerEtatCompte(keycloakUserId: string, activer: boolean): Observable<void> {
    const params = new HttpParams().set('activer', activer);
    return this.httpPut<void>(
      `${this.BASE}/utilisateurs/${keycloakUserId}/activer`,
      null,
      { params },
    );
  }

  /** GET /keycloak-admin/utilisateurs/{id}/groupes */
  getGroupesUtilisateur(keycloakUserId: string): Observable<GroupRepresentation[]> {
    return this.httpGet<GroupRepresentation[]>(
      `${this.BASE}/utilisateurs/${keycloakUserId}/groupes`,
    );
  }

  // ==========================================================================
  // UTILISATEURS — Required Actions & Sessions
  // ==========================================================================

  /** PATCH /keycloak-admin/utilisateurs/{id}/required-actions/{action} */
  ajouterRequiredAction(keycloakUserId: string, action: string): Observable<void> {
    return this.httpPatch<void>(
      `${this.BASE}/utilisateurs/${keycloakUserId}/required-actions/${action}`,
      null,
    );
  }

  /** DELETE /keycloak-admin/utilisateurs/{id}/required-actions/{action} */
  retirerRequiredAction(keycloakUserId: string, action: string): Observable<void> {
    return this.httpDelete<void>(
      `${this.BASE}/utilisateurs/${keycloakUserId}/required-actions/${action}`,
    );
  }

  /** GET /keycloak-admin/utilisateurs/{id}/sessions */
  getSessionsUtilisateur(keycloakUserId: string): Observable<UserSessionRepresentation[]> {
    return this.httpGet<UserSessionRepresentation[]>(
      `${this.BASE}/utilisateurs/${keycloakUserId}/sessions`,
    );
  }

  /** DELETE /keycloak-admin/utilisateurs/{id}/sessions (logout global) */
  revoquerSessionsUtilisateur(keycloakUserId: string): Observable<void> {
    return this.httpDelete<void>(
      `${this.BASE}/utilisateurs/${keycloakUserId}/sessions`,
    );
  }

  // ==========================================================================
  // RÔLES
  // ==========================================================================

  /** POST /keycloak-admin/roles?nomRole=&description= */
  creerRole(nomRole: string, description?: string): Observable<void> {
    let params = new HttpParams().set('nomRole', nomRole);
    if (description) params = params.set('description', description);
    return this.httpPost<void>(`${this.BASE}/roles`, null, { params });
  }

  /** GET /keycloak-admin/roles */
  listerRoles(): Observable<RoleRepresentation[]> {
    return this.httpGet<RoleRepresentation[]>(`${this.BASE}/roles`);
  }

  /** GET /keycloak-admin/roles/arbre */
  listerRolesArbre(): Observable<RoleNoeudDto[]> {
    return this.httpGet<RoleNoeudDto[]>(`${this.BASE}/roles/arbre`);
  }

  /** GET /keycloak-admin/roles/{nomRole} */
  getRoleParNom(nomRole: string): Observable<RoleRepresentation> {
    return this.httpGet<RoleRepresentation>(`${this.BASE}/roles/${nomRole}`);
  }

  /** PUT /keycloak-admin/roles/{nomRole}?nouveauNom=&description= */
  mettreAJourRole(
    nomRole: string,
    nouveauNom?: string,
    description?: string,
  ): Observable<void> {
    let params = new HttpParams();
    if (nouveauNom) params = params.set('nouveauNom', nouveauNom);
    if (description) params = params.set('description', description);
    return this.httpPut<void>(`${this.BASE}/roles/${nomRole}`, null, { params });
  }

  /** DELETE /keycloak-admin/roles/{nomRole} */
  supprimerRole(nomRole: string): Observable<void> {
    return this.httpDelete<void>(`${this.BASE}/roles/${nomRole}`);
  }

  /** POST /keycloak-admin/roles/{nomRole}/composites */
  ajouterSousRoles(nomRole: string, sousRoles: RoleRepresentation[]): Observable<void> {
    return this.httpPost<void>(`${this.BASE}/roles/${nomRole}/composites`, sousRoles);
  }

  /** GET /keycloak-admin/roles/{nomRole}/composites */
  getSousRoles(nomRole: string): Observable<RoleRepresentation[]> {
    return this.httpGet<RoleRepresentation[]>(`${this.BASE}/roles/${nomRole}/composites`);
  }

  /**
   * DELETE /keycloak-admin/roles/{nomRole}/composites
   * Corps JSON requis (liste des sous-rôles à retirer).
   */
  retirerSousRoles(nomRole: string, sousRoles: RoleRepresentation[]): Observable<void> {
    return this.httpDelete<void>(
      `${this.BASE}/roles/${nomRole}/composites`,
      { body: sousRoles },
    );
  }

  /** POST /keycloak-admin/roles/assigner-historique */
  assignerRole(request: RoleKeycloakRequest): Observable<AccesKeycloakResponse> {
    return this.httpPost<AccesKeycloakResponse>(
      `${this.BASE}/roles/assigner-historique`,
      request,
    );
  }

  /** DELETE /keycloak-admin/roles/revoquer/{roleAccesId}?revoqueePar= */
  revoquerRole(
    roleAccesId: string,
    revoqueePar: string,
  ): Observable<AccesKeycloakResponse> {
    const params = new HttpParams().set('revoqueePar', revoqueePar);
    return this.httpDelete<AccesKeycloakResponse>(
      `${this.BASE}/roles/revoquer/${roleAccesId}`,
      { params },
    );
  }

  revoquerRoleDirect(
    keycloakUserId: string,
    roleId: string,
    roleNom: string,
  ): Observable<void> {
    const params = new HttpParams()
      .set('keycloakUserId', keycloakUserId)
      .set('roleId', roleId)
      .set('roleNom', roleNom);
    return this.httpDelete<void>(`${this.BASE}/roles/revoquer-direct`, { params });
  }

  /** GET /keycloak-admin/roles/actifs/{utilisateurId} */
  getRolesActifs(utilisateurId: string): Observable<AccesKeycloakResponse[]> {
    return this.httpGet<AccesKeycloakResponse[]>(
      `${this.BASE}/roles/actifs/${utilisateurId}`,
    );
  }

  /** GET /keycloak-admin/roles/historique/{utilisateurId} */
  getHistoriqueRoles(utilisateurId: string): Observable<AccesKeycloakResponse[]> {
    return this.httpGet<AccesKeycloakResponse[]>(
      `${this.BASE}/roles/historique/${utilisateurId}`,
    );
  }

  /** POST /keycloak-admin/roles/assigner-groupe?groupeId=&roleId=&roleNom= */
  assignerRoleAuGroupe(
    groupeId: string,
    roleId: string,
    roleNom: string,
  ): Observable<void> {
    const params = new HttpParams()
      .set('groupeId', groupeId)
      .set('roleId', roleId)
      .set('roleNom', roleNom);
    return this.httpPost<void>(`${this.BASE}/roles/assigner-groupe`, null, { params });
  }

  /** DELETE /keycloak-admin/roles/revoquer-groupe?groupeId=&roleId=&roleNom= */
  revoquerRoleDuGroupe(
    groupeId: string,
    roleId: string,
    roleNom: string,
  ): Observable<void> {
    const params = new HttpParams()
      .set('groupeId', groupeId)
      .set('roleId', roleId)
      .set('roleNom', roleNom);
    return this.httpDelete<void>(`${this.BASE}/roles/revoquer-groupe`, { params });
  }

  /** GET /keycloak-admin/roles/groupe/{groupeId} */
  getRolesGroupe(groupeId: string): Observable<RoleRepresentation[]> {
    return this.httpGet<RoleRepresentation[]>(`${this.BASE}/roles/groupe/${groupeId}`);
  }

  // ==========================================================================
  // GROUPES
  // ==========================================================================

  /** POST /keycloak-admin/groupes?nomGroupe= */
  creerGroupe(nomGroupe: string): Observable<string> {
    const params = new HttpParams().set('nomGroupe', nomGroupe);
    return this.httpPost<string>(`${this.BASE}/groupes`, null, { params });
  }

  /** POST /keycloak-admin/groupes/{parentId}/sous-groupes?nomSousGroupe= */
  creerSousGroupe(groupeParentId: string, nomSousGroupe: string): Observable<string> {
    const params = new HttpParams().set('nomSousGroupe', nomSousGroupe);
    return this.httpPost<string>(
      `${this.BASE}/groupes/${groupeParentId}/sous-groupes`,
      null,
      { params },
    );
  }

  /** GET /keycloak-admin/groupes */
  listerGroupes(): Observable<GroupRepresentation[]> {
    return this.httpGet<GroupRepresentation[]>(`${this.BASE}/groupes`);
  }

  /** GET /keycloak-admin/groupes/arbre */
  listerGroupesArbre(): Observable<GroupRepresentation[]> {
    return this.httpGet<GroupRepresentation[]>(`${this.BASE}/groupes/arbre`);
  }

  /** GET /keycloak-admin/groupes/{groupeId} */
  getGroupe(groupeId: string): Observable<GroupRepresentation> {
    return this.httpGet<GroupRepresentation>(`${this.BASE}/groupes/${groupeId}`);
  }

  /** GET /keycloak-admin/groupes/{groupeId}/arbre */
  getGroupeArbre(groupeId: string): Observable<GroupRepresentation> {
    return this.httpGet<GroupRepresentation>(`${this.BASE}/groupes/${groupeId}/arbre`);
  }

  /** GET /keycloak-admin/groupes/{groupeId}/sous-groupes */
  listerSousGroupes(groupeId: string): Observable<GroupRepresentation[]> {
    return this.httpGet<GroupRepresentation[]>(
      `${this.BASE}/groupes/${groupeId}/sous-groupes`,
    );
  }

  /** GET /keycloak-admin/groupes/{groupeId}/membres */
  getMembresGroupe(groupeId: string): Observable<UserRepresentation[]> {
    return this.httpGet<UserRepresentation[]>(
      `${this.BASE}/groupes/${groupeId}/membres`,
    );
  }

  /** PUT /keycloak-admin/groupes/{groupeId}/renommer?nouveauNom= */
  renommerGroupe(groupeId: string, nouveauNom: string): Observable<void> {
    const params = new HttpParams().set('nouveauNom', nouveauNom);
    return this.httpPut<void>(
      `${this.BASE}/groupes/${groupeId}/renommer`,
      null,
      { params },
    );
  }

  /** DELETE /keycloak-admin/groupes/{groupeId} */
  supprimerGroupe(groupeId: string): Observable<void> {
    return this.httpDelete<void>(`${this.BASE}/groupes/${groupeId}`);
  }

  /** POST /keycloak-admin/groupes/{groupeId}/membres/{keycloakUserId} */
  ajouterDansGroupe(groupeId: string, keycloakUserId: string): Observable<void> {
    return this.httpPost<void>(
      `${this.BASE}/groupes/${groupeId}/membres/${keycloakUserId}`,
      null,
    );
  }

  /** DELETE /keycloak-admin/groupes/{groupeId}/membres/{keycloakUserId} */
  retirerDuGroupe(groupeId: string, keycloakUserId: string): Observable<void> {
    return this.httpDelete<void>(
      `${this.BASE}/groupes/${groupeId}/membres/${keycloakUserId}`,
    );
  }

  /** PUT /keycloak-admin/groupes/{groupeId}/attributs */
  mettreAJourAttributsGroupe(
    groupeId: string,
    attributs: Record<string, string[]>,
  ): Observable<void> {
    return this.httpPut<void>(
      `${this.BASE}/groupes/${groupeId}/attributs`,
      attributs,
    );
  }

  /** PATCH /keycloak-admin/groupes/{groupeId}/attributs/{cle} */
  ajouterOuRemplacerAttributGroupe(
    groupeId: string,
    cle: string,
    valeurs: string[],
  ): Observable<void> {
    return this.httpPatch<void>(
      `${this.BASE}/groupes/${groupeId}/attributs/${cle}`,
      valeurs,
    );
  }

  /** POST /keycloak-admin/groupes/assigner */
  assignerGroupe(request: GroupeKeycloakRequest): Observable<AccesKeycloakResponse> {
    return this.httpPost<AccesKeycloakResponse>(
      `${this.BASE}/groupes/assigner`,
      request,
    );
  }

  /** DELETE /keycloak-admin/groupes/revoquer/{groupeAccesId}?revoqueePar= */
  revoquerGroupe(
    groupeAccesId: string,
    revoqueePar: string,
  ): Observable<AccesKeycloakResponse> {
    const params = new HttpParams().set('revoqueePar', revoqueePar);
    return this.httpDelete<AccesKeycloakResponse>(
      `${this.BASE}/groupes/revoquer/${groupeAccesId}`,
      { params },
    );
  }

  /** GET /keycloak-admin/groupes/actifs/{utilisateurId} */
  getGroupesActifs(utilisateurId: string): Observable<AccesKeycloakResponse[]> {
    return this.httpGet<AccesKeycloakResponse[]>(
      `${this.BASE}/groupes/actifs/${utilisateurId}`,
    );
  }

  /** GET /keycloak-admin/groupes/historique/{utilisateurId} */
  getHistoriqueGroupes(utilisateurId: string): Observable<AccesKeycloakResponse[]> {
    return this.httpGet<AccesKeycloakResponse[]>(
      `${this.BASE}/groupes/historique/${utilisateurId}`,
    );
  }

  // ── Consultation supplémentaire ─────────────────────────────────────────

  /** GET /keycloak-admin/utilisateurs/lister/tous (pagination automatique) */
  listerTousLesUtilisateurs(): Observable<UserRepresentation[]> {
    return this.httpGet<UserRepresentation[]>(`${this.BASE}/utilisateurs/lister/tous`);
  }

  /** GET /keycloak-admin/groupes/{groupeId}/membres/pagine?first=0&max=20 */
  getMembresGroupePagine(
    groupeId: string,
    first = 0,
    max = 20,
  ): Observable<UserRepresentation[]> {
    const params = new HttpParams().set('first', first).set('max', max);
    return this.httpGet<UserRepresentation[]>(
      `${this.BASE}/groupes/${groupeId}/membres/pagine`,
      { params },
    );
  }

  /** GET /keycloak-admin/utilisateurs/{keycloakUserId}/roles */
  getRolesUtilisateur(keycloakUserId: string): Observable<RoleRepresentation[]> {
    return this.httpGet<RoleRepresentation[]>(
      `${this.BASE}/utilisateurs/${keycloakUserId}/roles`,
    );
  }

  /**
   * Planifie la désactivation automatique d'un compte.
   * PATCH /keycloak-admin/utilisateurs/{utilisateurId}/date-fin-acces
   */
  planifierDesactivation(
    utilisateurId: string,
    dateFinAcces: string,
  ): Observable<Record<string, string>> {
    const params = new HttpParams().set('dateFinAcces', dateFinAcces);
    return this.httpPatch<Record<string, string>>(
      `${this.BASE}/utilisateurs/${utilisateurId}/date-fin-acces`,
      null,
      { params },
    );
  }

  /** GET /keycloak-admin/utilisateurs/lister/enrichi */
  listerUtilisateursEnrichis(): Observable<UtilisateurEnrichiDto[]> {
    return this.httpGet<UtilisateurEnrichiDto[]>(`${this.BASE}/utilisateurs/lister/enrichi`);
  }

  /**
   * GET /v0.1/api/simo/personnes/sans-compte
   * Retourne les Personne de type EMPLOYE sans compte Keycloak/SIMO.
   */
  getPersonnesSansCompte(): Observable<PersonneSansCompteResponse[]> {
    return this.httpGet<PersonneSansCompteResponse[]>('personne/sans-compte');
  }

  /** GET /keycloak-admin/roles/{nomRole}/utilisateurs */
  getUtilisateursParRole(nomRole: string): Observable<UserRepresentation[]> {
    return this.httpGet<UserRepresentation[]>(`${this.BASE}/roles/${encodeURIComponent(nomRole)}/utilisateurs`);
  }

  /** GET /keycloak-admin/roles/{nomRole}/groupes */
  getGroupesParRole(nomRole: string): Observable<GroupRepresentation[]> {
    return this.httpGet<GroupRepresentation[]>(`${this.BASE}/roles/${encodeURIComponent(nomRole)}/groupes`);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// MODÈLE TypeScript à ajouter dans keycloak-admin.model.ts
// ═══════════════════════════════════════════════════════════════════════════

/** Réponse allégée pour la liste de sélection "Personne sans compte" */
export interface PersonneSansCompteResponse {
  id: string;
  nom?: string;
  prenoms?: string;
  email?: string;
  telephone?: string;
  sexe?: string;
  structure?: {
    id: string;
    nom?: string;
    libelle?: string;
    raisonSociale?: string;
  };
  poste?: {
    id: string;
    nom?: string;
    libelle?: string;
  };
  dateCreation?: string;
}